import type { IDBPDatabase } from 'idb';
import log from 'loglevel';

import {
  type AppDBSchema,
  COMPOSE_SNAPSHOTS_DRAFT_INDEX,
  COMPOSE_SNAPSHOTS_STORE,
  COMPOSE_SNAPSHOTS_USER_INDEX,
  indexedDBAdapter,
} from './indexedDBAdapter';
import { ComposeSnapshotChangeType } from '../../enums/composeSnapshotChangeType';
import { ComposeSnapshotStatus } from '../../enums/composeSnapshotStatus';
import type {
  ComposeSnapshot,
  ComposeSnapshotMatch,
} from '../../models/composeSnapshot';
import {
  COMPOSE_SNAPSHOT_MAX_PER_USER,
  COMPOSE_SNAPSHOT_TTL_MS,
} from '../../utils/constants/drafts';

const LOG_PREFIX = '[composeSnapshotCache]';

type Db = IDBPDatabase<AppDBSchema>;

export type ComposeSnapshotChange =
  | { type: ComposeSnapshotChangeType.PUT; snapshot: ComposeSnapshot }
  | {
      type: ComposeSnapshotChangeType.DELETE;
      userId: string;
      sessionId: string;
    };

type ComposeSnapshotListener = (change: ComposeSnapshotChange) => void;

const listeners = new Set<ComposeSnapshotListener>();

export const subscribeComposeSnapshotChanges = (
  listener: ComposeSnapshotListener,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const notify = (change: ComposeSnapshotChange): void => {
  for (const listener of [...listeners]) {
    try {
      listener(change);
    } catch (error) {
      log.warn(`${LOG_PREFIX} listener failed`, error);
    }
  }
};

const notifyDeleted = (userId: string, sessionId: string): void =>
  notify({ type: ComposeSnapshotChangeType.DELETE, userId, sessionId });

const withDb = async <T>(
  operation: string,
  fallback: T,
  run: (db: Db) => Promise<T>,
): Promise<T> => {
  try {
    return await run(await indexedDBAdapter.getConnection());
  } catch (error) {
    log.warn(`${LOG_PREFIX} ${operation} failed`, error);
    return fallback;
  }
};

const newestFirst = (a: ComposeSnapshot, b: ComposeSnapshot): number =>
  b.updatedAt - a.updatedAt;

const listForUser = (db: Db, userId: string): Promise<ComposeSnapshot[]> =>
  db.getAllFromIndex(
    COMPOSE_SNAPSHOTS_STORE,
    COMPOSE_SNAPSHOTS_USER_INDEX,
    userId,
  );

const deleteMany = async (
  db: Db,
  snapshots: ComposeSnapshot[],
): Promise<void> => {
  const tx = db.transaction(COMPOSE_SNAPSHOTS_STORE, 'readwrite');
  await Promise.all([
    ...snapshots.map((s) => tx.store.delete([s.userId, s.sessionId])),
    tx.done,
  ]);
  snapshots.forEach((s) => notifyDeleted(s.userId, s.sessionId));
};

// Tiers in priority order; the first tier with any match wins.
const buildMatchTiers = (
  match: ComposeSnapshotMatch,
): Array<(s: ComposeSnapshot) => boolean> => {
  const { draftId, sourceMessageId, handler, inboxEntryId } = match;
  const tiers: Array<(s: ComposeSnapshot) => boolean> = [];
  if (draftId) {
    tiers.push((s) => s.draftId === draftId);
  }
  if (sourceMessageId && handler) {
    tiers.push(
      (s) => s.sourceMessageId === sourceMessageId && s.handler === handler,
    );
  }
  if (inboxEntryId) {
    tiers.push(
      (s) =>
        s.inboxEntryId === inboxEntryId && !s.draftId && !s.sourceMessageId,
    );
  }
  return tiers;
};

export const putComposeSnapshot = (
  snapshot: ComposeSnapshot,
): Promise<boolean> =>
  withDb('put', false, async (db) => {
    await db.put(COMPOSE_SNAPSHOTS_STORE, snapshot);
    notify({ type: ComposeSnapshotChangeType.PUT, snapshot });
    return true;
  });

export const getComposeSnapshot = (
  userId: string,
  sessionId: string,
): Promise<ComposeSnapshot | undefined> =>
  withDb('get', undefined, (db) =>
    db.get(COMPOSE_SNAPSHOTS_STORE, [userId, sessionId]),
  );

export const findComposeSnapshot = (
  userId: string,
  match: ComposeSnapshotMatch,
): Promise<ComposeSnapshot | undefined> =>
  withDb('find', undefined, async (db) => {
    const snapshots = await listForUser(db, userId);
    for (const tier of buildMatchTiers(match)) {
      const matches = snapshots.filter(tier).sort(newestFirst);
      if (matches.length > 0) {
        return matches[0];
      }
    }
    return undefined;
  });

export const listDirtyComposeSnapshots = (
  userId: string,
): Promise<ComposeSnapshot[]> =>
  withDb('listDirty', [], async (db) =>
    (await listForUser(db, userId))
      .filter((s) => s.status === ComposeSnapshotStatus.DIRTY)
      .sort(newestFirst),
  );

export const deleteComposeSnapshot = (
  userId: string,
  sessionId: string,
): Promise<boolean> =>
  withDb('delete', false, async (db) => {
    await db.delete(COMPOSE_SNAPSHOTS_STORE, [userId, sessionId]);
    notifyDeleted(userId, sessionId);
    return true;
  });

export const deleteComposeSnapshotsByDraftId = (
  userId: string,
  draftId: string,
  exceptSessionIds: string | readonly string[] = [],
): Promise<boolean> =>
  withDb('deleteByDraftId', false, async (db) => {
    const excluded = new Set([exceptSessionIds].flat());
    const withDraft = await db.getAllFromIndex(
      COMPOSE_SNAPSHOTS_STORE,
      COMPOSE_SNAPSHOTS_DRAFT_INDEX,
      draftId,
    );
    await deleteMany(
      db,
      withDraft.filter(
        (s) => s.userId === userId && !excluded.has(s.sessionId),
      ),
    );
    return true;
  });

export const pruneComposeSnapshots = (
  userId: string,
  exceptSessionIds: readonly string[] = [],
): Promise<boolean> =>
  withDb('prune', false, async (db) => {
    const cutoff = Date.now() - COMPOSE_SNAPSHOT_TTL_MS;
    const protectedIds = new Set(exceptSessionIds);
    const all = (await listForUser(db, userId))
      .filter((s) => !protectedIds.has(s.sessionId))
      .sort(newestFirst);
    const fresh = all.filter((s) => s.updatedAt >= cutoff);
    const expired = all.filter((s) => s.updatedAt < cutoff);
    const legacy = fresh.filter(
      (s) => s.status !== ComposeSnapshotStatus.DIRTY,
    );
    const dirtyFresh = fresh.filter(
      (s) => s.status === ComposeSnapshotStatus.DIRTY,
    );
    const overflow = dirtyFresh.slice(COMPOSE_SNAPSHOT_MAX_PER_USER);
    await deleteMany(db, [...expired, ...legacy, ...overflow]);
    return true;
  });
